import { RequestNoticeModal } from "@components/Dialogs";
import React, { useContext, useState } from "react";
import { useTheme } from "@hooks/useTheme";
import { PermissionContext } from "../../contexts/PermissionContext";
import OutlineButton from "../Buttons/OutlineButton";
import CheckedIcon from "../Icons/CheckedIcon";
import { ConfirmActionModal } from "./index";
import { useTranslation } from "react-i18next";
import { PrimaryButton } from "@components/Buttons";
import { createWorkflowTestId, workflowStepSlug } from "@utils/workflowTestId";

const WorkflowProgressVerificationReport = ({
  verificationReportSeq,
  projectId,
  steps = [],
  currentStep = 0,
  className = "",
  projectManagerId,
  onRequestReview,
  onRejectReview,
  onDistribute,
  onReReview,
  dataTestId = "workflow-progress-verification-report",
}) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const tid = createWorkflowTestId(dataTestId);
  const { canManageProjectWorkflow } = useContext(PermissionContext);
  const canManageWorkflow = canManageProjectWorkflow(projectManagerId);
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: "",
    description: "",
    confirmText: "",
    confirmButtonVariant: "primary",
    onConfirm: null,
  });
  const [showRequestNoticeModal, setShowRequestNoticeModal] = useState(false);

  const formatModalTitle = (title) => (typeof title === "string" ? title.toUpperCase() : title);

  const showConfirmation = (title, description, confirmText, confirmButtonVariant, onConfirm) => {
    setConfirmationModal({
      isOpen: true,
      title: formatModalTitle(title),
      description,
      confirmText,
      confirmButtonVariant,
      onConfirm,
    });
  };

  const closeConfirmation = () => {
    setConfirmationModal({
      isOpen: false,
      title: "",
      description: "",
      confirmText: "",
      confirmButtonVariant: "primary",
      onConfirm: null,
    });
  };

  const handleConfirmAction = () => {
    if (confirmationModal.onConfirm) {
      confirmationModal.onConfirm();
    }
    closeConfirmation();
  };

  const getStepStatus = (stepIndex) => {
    if (stepIndex < currentStep) return "completed";
    if (stepIndex === currentStep) return "current";
    return "pending";
  };

  const getConnectorClasses = (stepIndex) => {
    const status = getStepStatus(stepIndex);
    const baseClasses = "h-1 flex-1 transition-all duration-200";
    if (status === "completed") {
      return `${baseClasses} ${colors.bgStatusSuccess}`;
    }
    return `${baseClasses} ${colors.bgStatusMuted}`;
  };

  // Function to check if step should show CheckedIcon
  const shouldShowCheckedIcon = (stepIndex, step) => {
    const status = getStepStatus(stepIndex);
    // Show CheckedIcon for completed steps OR Ready step when it's current
    return status === "completed" || (status === "current" && step.label === "Distributed");
  };

  // Function to render buttons based on current step
  const renderStepButtons = () => {
    const currentStepData = steps[currentStep];
    if (!currentStepData || !canManageWorkflow) return null;

    const stepLabel = currentStepData.label;
    const stepSlug = workflowStepSlug(stepLabel);
    const stid = (name) => tid(`step-${stepSlug}-${name}`);

    switch (stepLabel) {
      case "Write":
        return (
          <div className="flex justify-between items-center mt-16" data-testid={stid("actions")}>
            <div className="flex gap-2"></div>
            <div className="flex gap-2">
              <PrimaryButton
                data-testid={stid("request-review-button")}
                onClick={() =>
                  showConfirmation(
                    t("project.verification-report.request-review"),
                    t("project.verification-report.confirm-request-review-body"),
                    t("project.verification-report.request-review"),
                    "primary",
                    onRequestReview,
                  )
                }
              >
                {t("project.verification-report.request-review")}
              </PrimaryButton>
            </div>
          </div>
        );

      case "Review":
        return (
          <div className="flex justify-between items-center mt-16" data-testid={stid("actions")}>
            <div className="flex gap-2">
              <button
                data-testid={stid("reject-button")}
                onClick={() =>
                  showConfirmation(
                    t("project.verification-report.reject-req-review"),
                    t("project.verification-report.confirm-reject-review-body"),
                    t("project.verification-report.reject"),
                    "danger",
                    onRejectReview,
                  )
                }
                className={`min-w-28 min-h-10 px-4 py-2 ${colors.bgButtonDanger} ${colors.textPrimary} text-sm font-medium transition-colors rounded-lg hover:opacity-80`}
              >
                {t("project.verification-report.reject")}
              </button>
            </div>
            <div className="flex gap-2">
              <PrimaryButton
                data-testid={stid("distribute-button")}
                onClick={() =>
                  showConfirmation(
                    t("project.verification-report.distribute"),
                    t("project.verification-report.confirm-distribute-body"),
                    t("project.verification-report.distribute"),
                    "primary",
                    onDistribute,
                  )
                }
                >
                {t("project.verification-report.distribute")}
              </PrimaryButton>
            </div>
          </div>
        );

      case "Distributed":
        return (
          <div className="flex justify-between items-center mt-16" data-testid={stid("actions")}>
            <div className="flex gap-2">
              <OutlineButton
                data-testid={stid("re-review-button")}
                size="md"
                onClick={() =>
                  showConfirmation(
                    t("project.verification-report.re-review"),
                    t("project.verification-report.confirm-re-review-body"),
                    t("project.verification-report.re-review"),
                    "danger",
                    onReReview,
                  )
                }
              >
                {t("project.verification-report.re-review")}
              </OutlineButton>
            </div>
            <div className="flex gap-2">
              {canManageWorkflow && (
                <PrimaryButton
                  data-testid={stid("request-notice-button")}
                  onClick={() => setShowRequestNoticeModal(true)}
                >
                  {t("project.verification-report.request-notice")}
                </PrimaryButton>
              )}
            </div>
          </div>
        );

      default:
        return (
          <div className="flex justify-between items-center mt-16">
            <div className="flex gap-2"></div>
          </div>
        );
    }
  };

  return (
    <div className={`w-full h-40 ${className}`} data-testid={tid("root")}>
      <div className="flex justify-center items-center mt-8" data-testid={tid("steps-track")}>
        {steps.map((step, index) => {
          const status = getStepStatus(index);
          const isActive = status === "current";
          const showCheckedIcon = shouldShowCheckedIcon(index, step);

          const Icon = step.icon;
          return (
            <React.Fragment key={index}>
              <div
                className="relative flex flex-col items-center"
                data-testid={tid(`step-${workflowStepSlug(step.label)}`)}
              >
                <div>
                  {showCheckedIcon ? <CheckedIcon size={56} /> : Icon && <Icon size={56} isActive={isActive} />}
                </div>
                <div
                  className={`absolute top-16 left-1/2 -translate-x-1/2 -translate-y-1/2 mt-2 min-w-30 text-center text-sm font-semibold ${isActive && step.label !== "Distributed" ? colors.textStatusActiveStep : colors.textPrimary}`}
                >
                  {t(`project.verification-report.step-${step.stepName}`)}
                </div>
              </div>

              {index < steps.length - 1 && <div className={`${getConnectorClasses(index)} max-w-40`} />}
            </React.Fragment>
          );
        })}
      </div>
      {renderStepButtons()}

      {/* Confirmation Modal */}
      <ConfirmActionModal
        data-testid={tid("confirmation-modal")}
        isOpen={confirmationModal.isOpen}
        onClose={closeConfirmation}
        onConfirm={handleConfirmAction}
        title={confirmationModal.title}
        description={confirmationModal.description}
        confirmText={confirmationModal.confirmText}
        confirmButtonVariant={confirmationModal.confirmButtonVariant}
      />

      {/* Request Notice Modal */}
      <RequestNoticeModal
        verificationReportSeq={verificationReportSeq}
        projectId={projectId}
        isOpen={showRequestNoticeModal}
        onClose={() => setShowRequestNoticeModal(false)}
      />
    </div>
  );
};

export default WorkflowProgressVerificationReport;
